const CLASSIC_SLOTS = ["QB", "RB", "RB", "WR", "WR", "WR", "TE", "FLEX", "DST"];
const SHOWDOWN_SLOTS = ["CPT", "FLEX", "FLEX", "FLEX", "FLEX", "FLEX"];
const PLAYER_COLUMNS = ["Position", "Name + ID", "Name", "ID", "Roster Position", "Salary", "Game Info", "TeamAbbrev", "AvgPointsPerGame"];

export function parseCsv(text) {
  const rows = [];
  let row = [], field = "", quoted = false;
  const source = String(text || "").replace(/^\uFEFF/, "");
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];
    if (quoted) {
      if (char === '"' && source[i + 1] === '"') { field += '"'; i += 1; }
      else if (char === '"') quoted = false;
      else field += char;
    } else if (char === '"') quoted = true;
    else if (char === ",") { row.push(field); field = ""; }
    else if (char === "\n") { row.push(field.replace(/\r$/, "")); rows.push(row); row = []; field = ""; }
    else field += char;
  }
  if (quoted) throw new Error("CSV contains an unterminated quoted field.");
  if (field || row.length) { row.push(field.replace(/\r$/, "")); rows.push(row); }
  while (rows.length > 1 && rows[rows.length - 1].every((value) => value === "")) rows.pop();
  return rows;
}

function sameSlots(actual, expected) {
  return actual.length === expected.length && actual.every((value, index) => value === expected[index]);
}

export function parseDraftKingsTemplate(text, fileName = "contest.csv") {
  const rows = parseCsv(text);
  const errors = [];
  if (rows.length < 9) errors.push("DraftKings template must contain the lineup header, instructions, player header, and player rows.");
  const rawHeader = rows[0] || [];
  const firstBlank = rawHeader.indexOf("");
  const lineupHeader = rawHeader.slice(0, firstBlank < 0 ? rawHeader.length : firstBlank);
  const contestType = sameSlots(lineupHeader, CLASSIC_SLOTS) ? "CLASSIC" : sameSlots(lineupHeader, SHOWDOWN_SLOTS) ? "SHOWDOWN" : null;
  if (!contestType) errors.push(`Unsupported lineup header: ${lineupHeader.join(", ") || "empty"}.`);

  const playerHeaderRow = rows[7] || [];
  const offset = playerHeaderRow.indexOf("Position");
  if (offset < 0 || !PLAYER_COLUMNS.every((name, index) => playerHeaderRow[offset + index] === name)) {
    errors.push("Row 8 does not contain the expected DraftKings player-pool columns.");
  }

  const players = [];
  const ids = new Set();
  if (offset >= 0) rows.slice(8).forEach((row, rowIndex) => {
    if (!row.some(Boolean)) return;
    const values = Object.fromEntries(PLAYER_COLUMNS.map((name, index) => [name, row[offset + index] ?? ""]));
    const salary = Number(values.Salary);
    const rosterPositions = values["Roster Position"].split("/").map((value) => value.trim()).filter(Boolean);
    const prefix = `Player row ${rowIndex + 9}`;
    if (!values.Name || !values.ID) errors.push(`${prefix} is missing Name or ID.`);
    if (!Number.isInteger(salary) || salary <= 0) errors.push(`${prefix} has an invalid salary.`);
    if (!rosterPositions.length) errors.push(`${prefix} has no roster eligibility.`);
    if (ids.has(values.ID)) errors.push(`${prefix} repeats ID ${values.ID}.`);
    ids.add(values.ID);
    players.push({
      id: values.ID,
      nameId: values["Name + ID"],
      name: values.Name,
      position: values.Position,
      rosterPositions,
      salary,
      gameInfo: values["Game Info"],
      team: values.TeamAbbrev,
      avgPoints: Number(values.AvgPointsPerGame) || 0,
      sourceRow: rowIndex + 9,
    });
  });
  if (!players.length) errors.push("The template contains no player records.");
  if (contestType === "SHOWDOWN") {
    const byIdentity = new Map();
    players.forEach((player) => {
      const key = `${player.name}\u0000${player.team}`;
      const positions = byIdentity.get(key) || new Set();
      player.rosterPositions.forEach((position) => positions.add(position));
      byIdentity.set(key, positions);
    });
    if (![...byIdentity.values()].some((positions) => positions.has("CPT") && positions.has("FLEX"))) {
      errors.push("Showdown template does not contain distinct CPT and FLEX records.");
    }
  }
  return {
    fileName, contestType, lineupHeader, rawLineupHeader: rawHeader, playerHeaderRow,
    salaryCap: 50000, players, errors,
    teams: [...new Set(players.map((player) => player.team).filter(Boolean))].sort(),
    games: [...new Set(players.map((player) => player.gameInfo).filter(Boolean))].sort(),
    positions: [...new Set(players.map((player) => player.position).filter(Boolean))].sort(),
  };
}

function csvCell(value) {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function exportDraftKingsLineups(contract, lineups) {
  return [contract.lineupHeader, ...lineups.map((lineup) => lineup.slots.map((entry) => entry.player.id))]
    .map((row) => row.map(csvCell).join(","))
    .join("\r\n") + "\r\n";
}

export function parseDraftKingsExport(text, contract) {
  const rows = parseCsv(text);
  const errors = [];
  if (!sameSlots(rows[0] || [], contract.lineupHeader)) errors.push("Export roster header differs from the uploaded template.");
  const playerById = new Map(contract.players.map((player) => [player.id, player]));
  const lineups = rows.slice(1).filter((row) => row.some(Boolean)).map((row, index) => {
    if (row.length !== contract.lineupHeader.length) errors.push(`Export lineup ${index + 1} has ${row.length} columns; expected ${contract.lineupHeader.length}.`);
    return {
      slots: contract.lineupHeader.map((slot, slotIndex) => ({ slot, player: playerById.get(row[slotIndex]) || null })),
    };
  });
  lineups.forEach((lineup, index) => lineup.slots.forEach(({ player }, slotIndex) => {
    if (!player) errors.push(`Export lineup ${index + 1}, ${contract.lineupHeader[slotIndex]} contains an ID outside the uploaded template.`);
  }));
  return { lineups, errors };
}

export const draftKingsRosterSlots = { CLASSIC: CLASSIC_SLOTS, SHOWDOWN: SHOWDOWN_SLOTS };
